import { isFeatureEnabled, isFeatureNotDisabled } from '@/utils/alerts_access'
import { effectiveUser, computeEffectiveAdmin } from '@/utils/view_as'

// Shared definition of the Maps section pages, used by both the map shell's left
// nav and the map dashboard cards so they stay in sync.
export function buildMapItems(user, viewAs) {
  // muggle preview: gate against the customer-admin twin (see utils/view_as).
  const isEffectiveAdmin = computeEffectiveAdmin(user, viewAs)
  user = effectiveUser(user, viewAs)
  const tenant = user.tenant
  const tenantType = user.types && user.types[tenant]
  const isTbn = tenantType === 'TBN'
  // CM/TBN/FASTTRAK tenants run live bookings; FLEET and CM-FLEET tenants are
  // telematics-only, so booking-driven map pages (future positions, booking
  // assignments) don't apply to them.
  const hasBookingPlatform = ['CM', 'TBN', 'FASTTRAK'].includes(tenantType)
  const telematicsType = user.telematics_types && user.telematics_types[tenant]

  const tenantFeatures = (user.features && user.features[tenant]) || {}
  // a tenant can take the fleet map away from its drivers
  // (maps_driver_fleet_map_enabled, on unless explicitly turned off). drivers get
  // the fleet map and nothing else, so turning it off leaves them no Maps pages
  // at all -- which is the point, and what empties the section everywhere this
  // list feeds: the map shell nav, the map dashboard cards, the site map's Maps
  // section, and the favorites those pages back.
  if (user.role === 'driver' && !isFeatureNotDisabled(tenantFeatures.maps_driver_fleet_map_enabled)) {
    return []
  }

  const items = [
    { name: 'Fleet Map', route: 'map_fleet', icon: 'fa-map-marker',
      description: "Live map of your fleet's current vehicle locations and statuses." },
  ]
  // drivers get the fleet map and nothing else. The classic navbar linked them
  // a single standalone map (navbar.vue:267-270); the horizon maps tree opened
  // the whole set to them as a deliberate IA change (see nav/registry.js's
  // header note), which the rollout reversed -- future positions, booking
  // assignments and the link builder are dispatch tools, not driver ones.
  // Returning early here covers the map shell's own nav, the map dashboard
  // cards and the site map's Maps section in one place; the horizon rail has
  // its own copy of this gate in nav/registry.js.
  if (user.role === 'driver') return items
  if (hasBookingPlatform && (isEffectiveAdmin || isTbn)) {
    items.push({ name: 'Future Map', route: 'map_future', icon: 'fa-clock-o',
      description: 'Projected vehicle positions and upcoming dispatch coverage.' })
  }
  items.push({ name: 'Vehicle Trip History', route: 'map_trip_history', icon: 'fa-road',
    description: "Replay a vehicle's historical trips, stops, and geofences on the map." })
  if (telematicsType === 'SAMSARA' || isTbn) {
    items.push({ name: 'Vehicle Route History', route: 'map_route_history', icon: 'fa-location-arrow',
      description: 'Review completed vehicle routes for SAMSARA-equipped vehicles.' })
  }
  if (hasBookingPlatform) {
    items.push({ name: 'Vehicle Booking Map', icon: 'fa-calendar', external: true,
      path: '/booking-track/' + tenant + (isTbn ? '?type=TBN' : ''),
      description: 'Map vehicles against their assigned bookings and routes.' })
  }
  // rollout-gated per tenant (maps_link_builder_enabled, off by default);
  // staff always see it. the router guard applies the same rule.
  if (isEffectiveAdmin || isFeatureEnabled(tenantFeatures.maps_link_builder_enabled)) {
    items.push({ name: 'Map Link Builder', route: 'map_link_builder', icon: 'fa-link',
      description: 'Create shareable live-map links for selected vehicles and a time window.' })
  }
  return items
}
